import React from "react";
import {
  Form,
  Input,
  DatePicker,
  TimePicker,
  Button,
  Alert,
  Space,
  Divider,
  Skeleton,
  Typography,
  Row,
  Col,
  Tag,
  Select,
  Radio,
} from "antd";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import { getRooms } from "store/slices/roomsSlice";
import {
  getInstructors,
  selectAssignableInstructors,
} from "store/slices/instructorsSlice";
import { useEffect } from "react";
import {
  GlobalOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  TeamOutlined,
} from "@ant-design/icons";

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const typeLabels = {
  online: {
    color: "blue",
    icon: <GlobalOutlined />,
    label: "Séance en ligne",
  },
  onsite: {
    color: "green",
    icon: <EnvironmentOutlined />,
    label: "Séance présentielle",
  },
  special: {
    color: "orange",
    icon: <HomeOutlined />,
    label: "Séance spéciale",
  },
};

const sessionStatusOptions = [
  "Not started",
  "In progress",
  "Paused",
  "Completed",
];

const getSessionStatusLabel = (status, t) => {
  switch (status) {
    case "In progress":
      return t("calendars.sessionStatus.inProgress", "In progress");
    case "Paused":
      return t("calendars.sessionStatus.paused", "Paused");
    case "Completed":
      return t("calendars.sessionStatus.completed", "Completed");
    default:
      return t("calendars.sessionStatus.notStarted", "Not started");
  }
};

const normalizeInstructorOption = (entry, t) => {
  if (!entry) return null;

  if (typeof entry === "string") {
    return {
      id: entry,
      label: t("calendars.form.instructor.unknown", "Instructeur inconnu"),
    };
  }

  const id = entry?._id || entry?.id;
  if (!id) return null;

  return {
    id,
    label:
      entry?.fullName ||
      entry?.email ||
      t("calendars.form.instructor.unknown", "Instructeur inconnu"),
  };
};

const CalendarForm = ({
  initialValues = {},
  onFinish,
  onCancel,
  loading = false,
  error = null,
  isEdit = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form?.useForm();

  const { rooms, loading: loadingRooms } = useSelector((state) => state?.rooms);
  const instructors = useSelector(selectAssignableInstructors);
  const loadingInstructors = useSelector(
    (state) => state?.instructors?.loading
  );
  const instructorOptionsMap = new Map();

  const addInstructorOption = (entry) => {
    const option = normalizeInstructorOption(entry, t);
    if (option && !instructorOptionsMap.has(option.id)) {
      instructorOptionsMap.set(option.id, option);
    }
  };

  if (Array.isArray(initialValues?.groupData?.instructors)) {
    initialValues.groupData.instructors.forEach((entry) => addInstructorOption(entry));
  } else if (initialValues?.groupData?.instructors) {
    addInstructorOption(initialValues.groupData.instructors);
  }

  addInstructorOption(initialValues?.groupData?.instructor);

  if (Array.isArray(instructors)) {
    instructors.forEach((entry) => addInstructorOption(entry));
  }

  const instructorOptions = Array.from(instructorOptionsMap.values());

  // Charger les salles au montage si besoin
  useEffect(() => {
    dispatch(getRooms());
    dispatch(getInstructors());
  }, [dispatch]);

  // Pré-remplir le formulaire avec la date initiale si fournie
  // useEffect(() => {
  //   console.log("calandar initial values : ", initialValues);

  //   if (initialValues) {
  //     form.setFieldsValue({
  //       date: initialValues.initialDate
  //         ? dayjs(initialValues.initialDate)
  //         : undefined,
  //     });
  //   }
  // }, [initialValues, form]);
  useEffect(() => {
    if (initialValues) {
      let defaultInstructor =
        initialValues?.sessionInstructor ||
        initialValues?.sessionInstructors?.[0]?._id ||
        initialValues?.sessionInstructors?.[0] ||
        instructorOptions?.[0]?.id ||
        null;

      if (
        defaultInstructor &&
        typeof defaultInstructor === "object"
      ) {
        defaultInstructor = defaultInstructor?._id || defaultInstructor?.id || null;
      }

      form?.setFieldsValue({
        date: initialValues?.date ? dayjs(initialValues?.date) : undefined,
        startTime: initialValues?.startTime
          ? dayjs(initialValues?.startTime, "HH:mm")
          : undefined,
        endTime: initialValues?.endTime
          ? dayjs(initialValues?.endTime, "HH:mm")
          : undefined,
        url: initialValues?.url,
        salleId: initialValues?.salleId?._id || undefined,
        location: initialValues?.location,
        status: initialValues?.status || "Not started",
        sessionInstructor: defaultInstructor,
        isInsideWorkHours: initialValues?.isInsideWorkHours ?? false, // Default to false
      });
    }
  }, [initialValues, form, instructorOptions]);

  // Règles de validation
  const rules = {
    required: {
      required: true,
      message: t("common.validation.required", "Ce champ est obligatoire"),
    },
    url: {
      type: "url",
      message: t("common.validation.url", "Veuillez saisir une URL valide"),
    },
  };

  // Validation personnalisée pour la date (pas de date dans le passé)
  const validateDate = (_, value) => {
    if (isEdit && value && value?.isBefore(dayjs()?.startOf("day"))) {
      return Promise?.reject(
        t(
          "calendars.form.validation.pastDate",
          "La date ne peut pas être dans le passé",
        ),
      );
    }
    return Promise?.resolve();
  };

  // Soumission du formulaire
  const handleSubmit = (values) => {
    // Vérifications de validation côté client
    const formattedValues = {
      ...values,
      groupId: initialValues?.groupId?._id || initialValues?.groupId,
      type: initialValues?.type,
      status: values?.status || initialValues?.status || "Not started",
      date: values?.date ? values?.date?.format("YYYY-MM-DD") : undefined,
      startTime: values?.startTime
        ? values?.startTime?.format("HH:mm")
        : undefined,
      endTime: values?.endTime ? values?.endTime?.format("HH:mm") : undefined,
      // Include session instructor(s) if selected
      sessionInstructor: values?.sessionInstructor || undefined,
    };

    // 1. Validation que l'heure de fin est après l'heure de début
    if (values?.startTime && values?.endTime) {
      const start = dayjs(`2000-01-01 ${values?.startTime?.format("HH:mm")}`);
      const end = dayjs(`2000-01-01 ${values?.endTime?.format("HH:mm")}`);

      // Si l'heure de fin est avant ou égale à l'heure de début
      if (end?.isBefore(start) || end?.isSame(start)) {
        form?.setFields([
          {
            name: "endTime",
            errors: [
              t(
                "calendars.form.validation.endTimeAfterStartTime",
                "L'heure de fin doit être après l'heure de début",
              ),
            ],
          },
        ]);
        return;
      }

      // 2. Validation de la durée maximale (1h30 = 90 minutes)
      // const durationMinutes = end.diff(start, "minute");
      // if (durationMinutes > 90) {
      //   form.setFields([
      //     {
      //       name: "endTime",
      //       errors: [
      //         t(
      //           "calendars.form.validation.maxDuration",
      //           "La durée maximale d'une séance est de 1h30"
      //         ),
      //       ],
      //     },
      //   ]);
      //   return;
      // }
    }

    onFinish(formattedValues);
  };

  // Affichage du type
  const typeInfo = typeLabels[initialValues?.type] || {
    color: "default",
    icon: null,
    label: initialValues?.type,
  };

  // Fonction pour formater l'erreur
  const formatErrorMessage = (errorObj) => {
    if (!errorObj) return null;

    // Si c'est juste une chaîne
    if (typeof errorObj === "string") {
      return errorObj;
    }

    // Si c'est un objet d'erreur avec une structure spécifique
    if (errorObj?.message) {
      return errorObj?.message;
    }

    // Fallback pour d'autres formats
    return JSON?.stringify(errorObj);
  };
  return (
    <Skeleton loading={loading || loadingRooms} active paragraph={{ rows: 8 }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          date: initialValues?.initialDate
            ? dayjs(initialValues?.initialDate)
            : undefined,
          status: initialValues?.status || "Not started",
          isInsideWorkHours: false, // Default to outside work hours
        }}
      >
        {error && (
          <Alert
            message={t("calendars.form.error", "Erreur")}
            description={formatErrorMessage(error)}
            type="error"
            style={{ marginBottom: "16px" }}
            showIcon
          />
        )}

        <Row gutter={16}>
          <Col xs={24} sm={24} md={12}>
            <Form.Item label={t("calendars.form.type", "Type de séance")}>
              <Tag
                color={typeInfo?.color}
                icon={typeInfo?.icon}
                style={{ fontSize: 16 }}
              >
                {t(`calendars.type.${initialValues?.type}`, typeInfo?.label)}
              </Tag>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12}>
            <Form.Item label={t("calendars.form.group", "Groupe")}>
              <Tag
                icon={<TeamOutlined />}
                color="geekblue"
                style={{ fontSize: 16 }}
              >
                {initialValues?.groupName ||
                  initialValues?.groupId?.groupName ||
                  t("calendars.form.group.unknown", "Groupe inconnu")}
              </Tag>
            </Form.Item>
          </Col>
        </Row>


        <Divider>{t("calendars.form.schedule", "Planification")}</Divider>

        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="status"
              label={t("calendars.form.status", "Session status")}
              rules={[rules?.required]}
            >
              <Select
                placeholder={t("calendars.form.status", "Session status")}
                options={sessionStatusOptions.map((status) => ({
                  value: status,
                  label: getSessionStatusLabel(status, t),
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="sessionInstructor"
              label={t("calendars.form.instructor", "Instructeur")}
              rules={instructorOptions.length > 0 ? [rules?.required] : []}
            >
              <Select
                placeholder={t("calendars.form.selectInstructor", "Sélectionner un instructeur")}
                disabled={loadingInstructors || instructorOptions.length === 0}
                loading={loadingInstructors}
                showSearch
                optionFilterProp="children"
              >
                {instructorOptions.map((instructor) => (
                  <Option key={instructor.id} value={instructor.id}>
                    {instructor.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="date"
              label={t("calendars.form.date", "Date")}
              rules={[rules?.required]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                disabledDate={
                  isEdit
                    ? (current) => current && current < dayjs()?.startOf("day")
                    : undefined
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="startTime"
              label={t("calendars.form.startTime", "Heure de début")}
              rules={[rules?.required]}
              // extra={
              //   <Text type="secondary">
              //     {t("calendars.form.maxDuration", "Durée max: 1h30")}
              //   </Text>
              // }
            >
              <TimePicker
                format="HH:mm"
                minuteStep={5}
                style={{ width: "100%" }}
                use12Hours={false}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="endTime"
              label={t("calendars.form.endTime", "Heure de fin")}
              dependencies={["startTime"]}
              validateFirst
              validateTrigger={["onChange", "onBlur"]}
            >
              <TimePicker
                format="HH:mm"
                minuteStep={5}
                style={{ width: "100%" }}
                use12Hours={false}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              name="isInsideWorkHours"
              label={t("calendars.form.workHours", "Horaires de travail")}
              rules={[rules?.required]}
            >
              <Radio.Group>
                <Radio value={true}>
                  {t("calendars.form.workHours.inside", "Pendant les heures de travail")}
                </Radio>
                <Radio value={false}>
                  {t("calendars.form.workHours.outside", "En dehors des heures de travail")}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        {initialValues?.type === "online" && (
          <Form.Item
            name="url"
            label={t("calendars.form.url", "Lien de la séance")}
            rules={[rules?.required, rules?.url]}
          >
            <Input placeholder="https://meet.google.com/..." />
          </Form.Item>
        )}

        {initialValues?.type === "onsite" && (
          <Form.Item
            name="salleId"
            label={t("calendars.form.room", "Salle")}
            rules={[rules?.required]}
          >
            <Select
              placeholder={t(
                "calendars.form.selectRoom",
                "Sélectionner une salle",
              )}
              loading={loadingRooms}
              disabled={loadingRooms}
            >
              {rooms &&
                rooms?.map((room) => (
                  <Option key={room?._id} value={room?._id}>
                    <Text strong>{room?.titled}</Text> ({room?.ability}{" "}
                    {t("calendars.form.capacity", "places")})
                  </Option>
                ))}
            </Select>
          </Form.Item>
        )}

        {initialValues?.type === "special" && (
          <Form.Item
            name="location"
            label={t("calendars.form.location", "Lieu")}
            rules={[rules?.required]}
          >
            <TextArea
              placeholder={t(
                "calendars.form.locationPlaceholder",
                "Adresse ou lieu de la séance",
              )}
              rows={2}
            />
          </Form.Item>
        )}

        <Divider />

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              {t(
                isEdit ? "common.update" : "common.create",
                isEdit ? "Modifier" : "Créer",
              )}
            </Button>

            <Button onClick={onCancel}>{t("common.cancel", "Annuler")}</Button>
          </Space>
        </Form.Item>
      </Form>
    </Skeleton>
  );
};

export default CalendarForm;
